import React from 'react';
import { useInView } from 'react-intersection-observer';
import { Translate } from 'react-localize-redux';
import styled, { css, keyframes } from 'styled-components';

import Heading, { HeadingContainer, SubHeading } from './heading';
import TARO_AND_HANAKO from './images/taro-and-hanako.png';

const slideInAnim = keyframes`
  100% {
    transform: translate(0, 0);
  }
`;

export const slideInTime = 0.7;

const Wrapper = styled.div`
  overflow-x: hidden;
`;

const Background = styled.section`
  color: #111111;
  position: relative;

  ${({ slideIn, slideInActive }) =>
    slideIn &&
    css`
      ${slideIn === 'left' &&
        css`
          transform: translate(100%, -2.5em);
        `}
      ${slideIn === 'right' &&
        css`
          transform: translate(-100%, -2.5em);
        `}

      ${slideInActive &&
        css`
          animation: ${slideInAnim} ${slideInTime}s cubic-bezier(0, 1, 0, 1)
            both;
        `}
    `}

  ${({ background, topClip }) =>
    background
      ? css`
          padding: 7.5em 0;
          background: ${background};
          ${topClip === 'left'
            ? css`
                clip-path: polygon(
                  0 2.5em,
                  100% 0,
                  100% calc(100% - 2.5em),
                  0 100%
                );
              `
            : css`
                clip-path: polygon(
                  0 0em,
                  100% 2.5em,
                  100% 100%,
                  0 calc(100% - 2.5em)
                );
              `};
        `
      : css`
          padding: 5em 0;
        `}
`;

const Container = styled.div`
  @media (min-width: 768px) {
    margin: 0 auto;
    max-width: 45em;

    ${props =>
      props.vertical &&
      css`
        align-items: center;
        display: flex;
      `}
  }
`;

const Content = styled.div`
  flex: 1;
  padding: 0 1.5em;
`;

const Section = ({
  slideIn = 'left',
  background,
  heading,
  headingInvert = false,
  headingPosition = 'left',
  subHeading,
  children
}) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.25
  });
  return (
    <Wrapper ref={ref}>
      <Background
        background={background}
        slideIn={slideIn}
        slideInActive={inView}
        topClip={headingPosition}
      >
        <Container vertical={headingPosition !== 'top'}>
          <HeadingContainer
            position={headingPosition}
            vertical={headingPosition !== 'top'}
          >
            <Heading
              invert={headingInvert}
              inView={inView}
              vertical={headingPosition !== 'top'}
            >
              {heading}
            </Heading>
            <SubHeading
              invert={headingInvert}
              inView={inView}
              vertical={headingPosition !== 'top'}
            >
              {subHeading}
            </SubHeading>
          </HeadingContainer>
          <Content>{children}</Content>
        </Container>
      </Background>
    </Wrapper>
  );
};

export default Section;

const StyledKomissaSaysSection = styled.div`
  background: #0c0c0c;
  border: 3px solid ${props => props.theme.primary};
  margin: 12em 1em 1em;
  padding: 0 1em 4em;
  position: relative;

  > *:nth-child(n + 3) {
    margin-top: 4em;
  }

  @media (min-width: 768px) {
    margin-left: auto;
    margin-right: auto;
    max-width: 30em;
  }
`;

const KomissaSays = styled.div`
  margin: -6.5em 0 2em;
`;

const textAnim = keyframes`
  0% {
    width: 0;
  }
  100% {
    width: 12em;
  }
`;

const KomissaText = styled.div`
  display: flex;
  justify-content: center;

  &::before {
    background: #ffffff;
    content: '';
    display: block;
    margin: 0.1em 0.5em 0 0;
    transform: rotate(-30deg);
    transform-origin: right bottom;
    width: 0.2em;

    opacity: 0;
    transition: opacity 0.1s ease-out 1s;
    ${({ inView }) =>
      inView &&
      css`
        opacity: 1;
      `}
  }

  &::after {
    background: #ffffff;
    content: '';
    display: block;
    margin: 0.1em 0 0 0.5em;
    transform: rotate(30deg);
    transform-origin: left bottom;
    width: 0.2em;

    opacity: 0;
    transition: opacity 0.1s ease-out 1s;
    ${({ inView }) =>
      inView &&
      css`
        opacity: 1;
      `}
  }

  > span {
    overflow: hidden;
    text-align: center;
    white-space: nowrap;
    width: 0;

    ${({ inView }) =>
      inView &&
      css`
        animation: ${textAnim} 1.5s steps(12, end) 1s both;
      `}
  }
`;

const bounce = keyframes`
  0%   { transform: scale(1, 1)       translateY(0); }
  10%  { transform: scale(1.1, 0.9)   translateY(0); }
  30%  { transform: scale(0.9, 1.1)   translateY(-50px); }
  50%  { transform: scale(1.05, 0.95) translateY(0); }
  57%  { transform: scale(1, 1)       translateY(-7px); }
  64%  { transform: scale(1, 1)       translateY(0); }
  100% { transform: scale(1, 1)       translateY(0); }
`;

const Komissa = styled.img`
  display: block;
  height: 8em;
  margin: 1em auto 0;

  ${({ inView }) =>
    inView &&
    css`
      animation: ${bounce} 1s cubic-bezier(0.28, 0.84, 0.42, 1) both;
    `}
`;

export const KomissaSaysSection = ({ children }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 1 });
  return (
    <StyledKomissaSaysSection>
      <KomissaSays>
        <KomissaText inView={inView}>
          <span>
            <Translate id="psyLandingPage.information.komissaSays" />
          </span>
        </KomissaText>
        <Komissa inView={inView} ref={ref} src={TARO_AND_HANAKO} />
      </KomissaSays>
      {children}
    </StyledKomissaSaysSection>
  );
};
